/* @flow */

import * as React from 'react';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import Button from 'app/components/commons/Button';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import Modal from 'app/components/commons/Modal';
import { resetUserErrors, selfExclude, userSignOut } from 'app/actions/userActions';

type Props = {
  email: string,
  onClearError: () => void,
  onSignout: () => void,
  onSubmit: () => void,
  selfExcluded?: boolean,
  selfExcluding: boolean,
  selfExcludingErrorMessage?: string
};

type State = {
  confirming: boolean
};

const Danger = styled.h3(({ theme }) => ({
  color: theme.color.white,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.color.danger,
  borderRadius: '5px 5px',
  textAlign: 'center',
  padding: theme.spacing.default,
  position: 'relative'
}));

const Label = styled.div(({ theme }) => ({
  color: 'gray',
  fontSize: theme.fontSize.large
}));

const EmailContent = styled.p(({ theme }) => ({
  borderBottom: '1px solid gray',
  paddingBottom: '5px',
  fontSize: theme.fontSize.large
}));

const Text = styled.p(({ theme }) => ({
  fontSize: theme.fontSize.large
}));

const WarningText = styled.h3(({ theme }) => ({
  color: theme.color.danger
}));

const ButtonContainer = styled.div({
  display: 'flex',
  justifyContent: 'center'
});

class AccountSelfExclusion extends React.Component<Props, State> {
  props: Props;

  state: State = {
    confirming: false
  };

  static defaultProps = {
    selfExcluded: false,
    selfExcludingErrorMessage: undefined
  };

  componentDidUpdate(prevProps: Props) {
    const { onSignout, selfExcluded } = this.props;

    if (selfExcluded && !prevProps.selfExcluded) {
      onSignout();
    }
  }

  confirmSubmit = () => this.setState({ confirming: true });

  handleCancel = () => {
    const { onClearError } = this.props;

    this.setState({ confirming: false });
    onClearError();
  };

  handleSubmit = () => {
    const { onSubmit } = this.props;
    onSubmit();
  };

  render() {
    const { email, selfExcluding, selfExcludingErrorMessage } = this.props;
    const { confirming } = this.state;

    return (
      <div className="page-container">
        <Helmet title="Self Exclusion" />
        <h2>Self Exclusion</h2>
        <Danger title="">
          Note: If you proceed with Self-Exclusion this action will be PERMANENT.
        </Danger>
        <h3>What happens if I Proceed?</h3>
        <ul>
          <li>
            <h3>All pending bets will stand.</h3>
          </li>
          <li>
            <h3>
              ACCOUNT BALANCE REMAINS UNCHANGED. YOU CAN REQUEST A WITHDRAWAL BY CONTACTING CUSTOMER
              SERVICES.
            </h3>
          </li>
          <li>
            <h3>You will cease to receive marketing communications within 24 hours.</h3>
          </li>
          <li>
            <WarningText>
              You will NOT be able to login to this account or create a new account.
            </WarningText>
          </li>
        </ul>
        <Label>Email Address:</Label>
        <EmailContent>{email}</EmailContent>
        <Text>Confirmation of permanent self-exclusion will be sent to this email address.</Text>
        <ButtonContainer>
          <Button
            block
            kind="danger"
            size="medium"
            onClick={this.confirmSubmit}
            style={{ width: '200px' }}
          >
            Exclude Myself
          </Button>
        </ButtonContainer>
        {confirming && (
          <Modal isOpen={confirming} onCancel={this.handleCancel} size="medium">
            <Text className="text-center">Are you sure you want to exclude yourself?</Text>
            <Text className="text-center">
              You will NOT be able to login to this account or create a new account.
            </Text>
            {selfExcludingErrorMessage && (
              <FormValidationMessage message={selfExcludingErrorMessage} />
            )}
            <Button block kind="danger" disabled={selfExcluding} onClick={this.handleSubmit}>
              Exclude Myself Now
            </Button>
            <Button block kind="cancel" onClick={this.handleCancel}>
              Cancel
            </Button>
          </Modal>
        )}
      </div>
    );
  }
}

function mapStateToProps({ user }) {
  return {
    email: user.currentUser.email,
    selfExcluded: user.currentUser.selfExcluded,
    selfExcluding: user.selfExcluding,
    selfExcludingErrorMessage: user.selfExcludingErrorMessage
  };
}

const actions = {
  onClearError: resetUserErrors,
  onSubmit: selfExclude,
  onSignout: userSignOut
};

export default connect(
  mapStateToProps,
  actions
)(AccountSelfExclusion);
